/** Styles de mise en scène : même leçon, même chronologie, habillage différent. */
import { loadFont as loadNunito } from "@remotion/google-fonts/Nunito";
import { loadFont as loadCaveat } from "@remotion/google-fonts/Caveat";

const nunito = loadNunito("normal", { weights: ["400", "700", "800"], subsets: ["latin", "latin-ext"] });
const caveat = loadCaveat("normal", { weights: ["400", "700"], subsets: ["latin", "latin-ext"] });

export type StyleId = "presentation" | "tableau" | "observation";
export type Format = "9:16" | "16:9";

export type Theme = {
  id: StyleId;
  bg: string;
  bgImage?: string;
  card: string;
  cardBorder: string;
  text: string;
  muted: string;
  accent: string;
  accentSoft: string;
  amber: string;
  amberSoft: string;
  fontFamily: string;
  /** Les mots apparaissent un à un (écriture) au lieu d'une phrase entière (fondu). */
  wordByWord: boolean;
  /** Filtre SVG appliqué aux figures et formules (trait de craie). */
  rough: boolean;
  formulaScale: number;
  /** Les polices manuscrites paraissent plus petites : facteur sur toutes les tailles de texte. */
  textScale: number;
  mafs: Record<string, string>;
  figureBg: string;
  curve: string;
  point: string;
};

export const THEMES: Record<StyleId, Theme> = {
  presentation: {
    id: "presentation",
    bg: "#0F1A33",
    bgImage: "radial-gradient(1200px 800px at 20% 0%, #1B2B52 0%, #0F1A33 60%)",
    card: "#16233F", cardBorder: "#263559",
    text: "#F4F6FA", muted: "#9AA6C2",
    accent: "#35C4B5", accentSoft: "rgba(53,196,181,0.16)",
    amber: "#F2B84B", amberSoft: "rgba(242,184,75,0.16)",
    fontFamily: nunito.fontFamily,
    wordByWord: false, rough: false, formulaScale: 2.8, textScale: 1,
    mafs: { "--mafs-bg": "#FFFFFF", "--mafs-fg": "#15213B", "--mafs-line-color": "#C9CFDA", "--grid-line-subdivision-color": "#EEF0F4", "--mafs-origin-color": "#15213B" },
    figureBg: "#FFFFFF", curve: "#0E6B66", point: "#E8A317",
  },
  observation: {
    id: "observation",
    bg: "#0B2228",
    bgImage: "radial-gradient(1400px 900px at 70% 40%, #113A3E 0%, #0B2228 65%)",
    card: "rgba(255,255,255,0.06)", cardBorder: "rgba(255,255,255,0.14)",
    text: "#EEF4F3", muted: "#9DB4B5",
    accent: "#35C4B5", accentSoft: "rgba(53,196,181,0.16)",
    amber: "#F2B84B", amberSoft: "rgba(242,184,75,0.16)",
    fontFamily: nunito.fontFamily,
    wordByWord: false, rough: false, formulaScale: 2.8, textScale: 1,
    mafs: { "--mafs-bg": "#FFFFFF", "--mafs-fg": "#15213B", "--mafs-line-color": "#C9CFDA", "--grid-line-subdivision-color": "#EEF0F4", "--mafs-origin-color": "#15213B" },
    figureBg: "#FFFFFF", curve: "#0E6B66", point: "#E8A317",
  },
  tableau: {
    id: "tableau",
    bg: "#213F36",
    bgImage: "radial-gradient(1400px 900px at 50% 40%, #2A4C41 0%, #1B342C 70%)",
    card: "rgba(255,255,255,0.04)", cardBorder: "rgba(243,241,232,0.55)",
    text: "#F3F1E8", muted: "rgba(243,241,232,0.7)",
    accent: "#F3F1E8", accentSoft: "rgba(243,241,232,0.12)",
    amber: "#F7C873", amberSoft: "rgba(247,200,115,0.16)",
    fontFamily: caveat.fontFamily,
    wordByWord: true, rough: true, formulaScale: 2.9, textScale: 1.22,
    mafs: { "--mafs-bg": "transparent", "--mafs-fg": "#F3F1E8", "--mafs-line-color": "rgba(243,241,232,0.22)", "--grid-line-subdivision-color": "rgba(243,241,232,0.08)", "--mafs-origin-color": "#F3F1E8" },
    figureBg: "transparent", curve: "#F3F1E8", point: "#F7C873",
  },
};

/** Bord « tracé à la main » (rayons irréguliers), utilisé par le style Tableau. */
export const handDrawnRadius = "255px 15px 225px 15px / 15px 225px 15px 255px";
